import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { 
  Search, 
  Filter, 
  ShoppingCart, 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  CheckCircle,
  Star,
  Package,
  Truck,
  Shield,
  Award,
  Settings,
  Building,
  BadgePercent,
  Zap,
  UserCheck,
  Plus,
  Minus
} from 'lucide-react';
import { heroData, immediatelyAvailableProducts, productCategories, companyInfo } from '../mock';
import { hasShopifyIntegration, getShopifyProductId, getShopifyVariantId } from '../shopifyProductMap';
import { getPricingInfo, getSavingsDisplay } from '../pricingData';
import { useCart } from '../contexts/CartContext';
import CartModal from './CartModal';
import ShopifyBuyButton from './ShopifyBuyButton';

const HomePage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const cart = useCart();
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productQuantities, setProductQuantities] = useState({});
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [quoteForm, setQuoteForm] = useState({
    product: '',
    quantity: '',
    urgency: 'standard'
  });
  const [isCartOpen, setIsCartOpen] = useState(false);

  // Get quantity for a product (defaults to 1 case)
  const getQuantity = (productId) => productQuantities[productId] || 1;
  
  // Update quantity for a product
  const updateQuantity = (productId, quantity) => {
    setProductQuantities(prev => ({
      ...prev,
      [productId]: Math.max(1, quantity)
    }));
  };

  // Filter and sort products
  const filteredProducts = useMemo(() => {
    let filtered = immediatelyAvailableProducts.filter(product => {
      const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          product.id.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = categoryFilter === 'all' || product.category === categoryFilter;
      return matchesSearch && matchesCategory;
    });

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'price':
          return a.inventory.pricePerUnit - b.inventory.pricePerUnit;
        case 'stock':
          return b.inventory.totalUnits - a.inventory.totalUnits;
        case 'popular':
          return b.popular ? 1 : -1;
        default:
          return a.name.localeCompare(b.name);
      }
    });

    return filtered;
  }, [searchQuery, categoryFilter, sortBy]);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${backendUrl}/api/contact/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(contactForm)
      });
      
      if (response.ok) {
        toast.success('Message sent! We\'ll get back to you within 24 hours.');
        setContactForm({ name: '', email: '', phone: '', company: '', message: '' });
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to send message. Please email us directly at aprxsupply@gmail.com');
    }
  };

  const handleQuoteRequest = (e) => {
    e.preventDefault();
    // Mock submission
    toast.success('Quote request submitted! We\'ll send you a detailed quote within 2 hours.');
    setQuoteForm({ product: '', quantity: '', urgency: 'standard' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3">
              <div className="relative cursor-pointer">
                {/* Use icon on mobile, full logo on desktop */}
                <img 
                  src="https://customer-assets.emergentagent.com/job_alberta-pharmacy-1/artifacts/euyis7mj_Logo%20-%20APS%20Icon%20-%20Large.png"
                  alt="Alberta Pharmacy Supply Logo"
                  className="block sm:hidden w-12 h-12 object-contain"
                />
                <img 
                  src="https://customer-assets.emergentagent.com/job_alberta-pharmacy-1/artifacts/tosp0yq2_Logo%20-%20APS%20-%20Large.png"
                  alt="Alberta Pharmacy Supply Logo"
                  className="hidden sm:block w-48 h-16 object-contain"
                />
              </div>
              <div className="sm:hidden">
                <h1 className="text-base font-bold text-slate-900">{companyInfo.name}</h1>
                <p className="text-xs text-slate-600">Calgary, AB</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              {/* Show Login/Register if not logged in, or user info + cart if logged in */}
              {!user || user.role === 'admin' ? (
                <>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="flex items-center space-x-1 sm:space-x-2 border-slate-300 text-slate-700 hover:bg-slate-50 transition-all text-xs sm:text-sm"
                    onClick={() => navigate('/login')}
                  >
                    <span>Login</span>
                  </Button>
                  <Button 
                    size="sm"
                    className="flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm font-semibold"
                    style={{ background: 'linear-gradient(135deg, #7C3AED, #F97316)' }}
                    onClick={() => navigate('/register')}
                  >
                    <Building className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span>Register</span>
                  </Button>
                </>
              ) : (
                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="hidden sm:flex text-xs sm:text-sm text-slate-700"
                    onClick={() => navigate('/orders')}
                  >
                    My Orders
                  </Button>
                  {/* Admin button for authorized users */}
                  {user && (user.role === 'admin' || user.email === 'aprxsupply@gmail.com') && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="hidden sm:flex text-xs sm:text-sm text-purple-700 font-semibold"
                      onClick={() => navigate('/admin')}
                    >
                      Admin
                    </Button>
                  )}
                  <span className="text-xs sm:text-sm text-slate-700 font-medium hidden md:inline">
                    {user.pharmacy_name}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="relative"
                    onClick={() => setIsCartOpen(true)}
                  >
                    <ShoppingCart className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Cart</span>
                    {cart.itemCount > 0 && (
                      <Badge className="absolute -top-2 -right-2 bg-red-500 text-white px-1.5 py-0.5 text-xs">
                        {cart.itemCount}
                      </Badge>
                    )}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="text-xs sm:text-sm"
                    onClick={onLogout}
                  >
                    Logout
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-8 sm:py-16 md:py-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="max-w-7xl mx-auto relative">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <div className="flex items-center justify-center mb-6 sm:mb-8 md:mb-10">
              <div className="relative">
                {/* Use icon on mobile, full logo on desktop - MUCH BIGGER */}
                <img 
                  src="https://customer-assets.emergentagent.com/job_alberta-pharmacy-1/artifacts/euyis7mj_Logo%20-%20APS%20Icon%20-%20Large.png"
                  alt="Alberta Pharmacy Supply Logo"
                  className="block sm:hidden w-40 h-40 object-contain"
                />
                <img 
                  src="https://customer-assets.emergentagent.com/job_alberta-pharmacy-1/artifacts/tosp0yq2_Logo%20-%20APS%20-%20Large.png"
                  alt="Alberta Pharmacy Supply Logo"
                  className="hidden sm:block w-full max-w-2xl h-48 object-contain"
                />
              </div>
            </div>
            <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-3 sm:mb-4 md:mb-6 px-2 text-slate-900 leading-tight">
              {heroData.title}
            </h1>
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-slate-700 mb-2 sm:mb-3 md:mb-4 px-2 font-medium">
              {heroData.subtitle}
            </p>
            <p className="text-sm sm:text-base md:text-lg text-slate-600 px-2">
              {heroData.description}
            </p>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 md:gap-8 mb-6 sm:mb-8 md:mb-12 mt-8 sm:mt-10 md:mt-12">
              {[
                { icon: BadgePercent, text: "Lowest prices in Alberta - Guaranteed", color: "purple" },
                { icon: Zap, text: "Next day Calgary delivery", color: "orange" },
                { icon: UserCheck, text: "Dedicated Account Manager", color: "slate" }
              ].map((feature, index) => (
                <div 
                  key={index} 
                  className="group bg-white rounded-lg sm:rounded-xl p-5 sm:p-6 md:p-8 border border-slate-200 hover:border-slate-300 transition-all duration-300 hover:shadow-lg"
                >
                  <div className="flex flex-col items-center text-center">
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full ${
                      feature.color === 'purple' ? 'bg-purple-100' :
                      feature.color === 'orange' ? 'bg-orange-100' :
                      'bg-slate-100'
                    } p-2.5 sm:p-3 mb-3 sm:mb-4`}>
                      <feature.icon className={`w-full h-full ${
                        feature.color === 'purple' ? 'text-purple-600' :
                        feature.color === 'orange' ? 'text-orange-600' :
                        'text-slate-600'
                      }`} strokeWidth={2} />
                    </div>
                    <p className="text-xs sm:text-sm md:text-base font-semibold text-slate-800 leading-snug">{feature.text}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="flex justify-center mt-6 sm:mt-8 md:mt-12">
              <Button 
                size="lg" 
                className="text-white px-6 sm:px-10 md:px-16 py-4 sm:py-5 md:py-6 text-sm sm:text-base md:text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 rounded-lg w-full sm:w-auto max-w-md"
                style={{ background: 'linear-gradient(135deg, #7C3AED, #F97316)' }}
                onClick={() => {
                  document.getElementById('products-section')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                <Package className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Shop Available Products
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Product Filters */}
      <section className="py-8 px-4 sm:px-6 lg:px-8 bg-white/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search products by name or code..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div className="flex gap-4">
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-48">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {productCategories.map(category => (
                    <SelectItem key={category.id} value={category.name}>
                      {category.name} ({category.count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">Name</SelectItem>
                  <SelectItem value="price">Price</SelectItem>
                  <SelectItem value="stock">Stock Level</SelectItem>
                  <SelectItem value="popular">Most Popular</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </section>

      {/* Product Grid */}
      <section className="py-10 sm:py-12 md:py-16 px-4 sm:px-6 lg:px-8 bg-white" id="products-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4 px-2 text-slate-900">
              Our Top Best Local Sellers
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-slate-600 px-2 max-w-2xl mx-auto">
              In stock for next day delivery • All products meet Health Canada requirements
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProducts.map(product => (
              <Card key={product.id} className="group hover:shadow-xl transition-all duration-300 bg-white border border-slate-200 hover:border-slate-300 rounded-xl overflow-hidden">
                {/* Product Image */}
                <div className="aspect-square sm:aspect-square bg-slate-50 flex items-center justify-center overflow-hidden">
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="w-full h-full object-contain p-4 sm:p-6 group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                
                <CardHeader className="pb-3 sm:pb-4 px-4 sm:px-6">
                  <div className="flex justify-between items-start mb-2 gap-2">
                    <Badge variant={product.popular ? "default" : "secondary"} className={`text-xs font-semibold ${product.popular ? "bg-purple-600 border-0" : "bg-slate-100 text-slate-700 border-0"}`}>
                      {product.popular ? "Most Popular" : product.category}
                    </Badge>
                    <Badge variant="outline" className="text-green-600 border-green-500 bg-green-50 text-xs whitespace-nowrap font-medium">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Calgary Stock
                    </Badge>
                  </div>
                  <CardTitle className="text-base sm:text-lg font-bold text-slate-900">{product.name}</CardTitle>
                  <p className="text-xs sm:text-sm text-slate-600 font-medium">{product.capacity}</p>
                  
                  {/* Competitive Pricing Display */}
                  {(() => {
                    const pricing = getPricingInfo(product.id);
                    const savings = getSavingsDisplay(product.id);
                    if (pricing && savings) {
                      return (
                        <div className="mt-4 p-3 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg border border-green-200">
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="text-slate-600">Competitor Price:</span>
                            <span className="line-through text-slate-500 font-medium">${pricing.mckessonPrice.toFixed(2)}</span>
                          </div>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-semibold text-slate-800">Our Price:</span>
                            <span className="text-2xl font-bold text-green-600">${pricing.apsPrice.toFixed(2)}</span>
                          </div>
                          <div className="flex items-center justify-center">
                            <Badge className="bg-gradient-to-r from-orange-500 to-orange-600 border-0 text-white text-xs font-bold px-3 py-1">
                              {savings.display}
                            </Badge>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </CardHeader>
                
                <CardContent>
                  <div className="space-y-4">
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-medium text-slate-600">Units/Case:</span>
                          <p className="font-semibold text-slate-900">{product.inventory.unitsPerCase}</p>
                        </div>
                        <div>
                          <span className="font-medium text-slate-600">Price/Unit:</span>
                          <p className="text-green-600 font-bold">${product.inventory.pricePerUnit.toFixed(3)}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <p className="text-sm font-medium text-slate-700 mb-2">Features:</p>
                      <div className="flex flex-wrap gap-1">
                        {product.features.slice(0, 3).map(feature => (
                          <Badge key={feature} variant="outline" className="text-xs">
                            {feature}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button 
                            variant="outline" 
                            className="flex-1"
                            onClick={() => setSelectedProduct(product)}
                          >
                            View Details
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl">
                          <DialogHeader>
                            <DialogTitle>{selectedProduct?.name}</DialogTitle>
                          </DialogHeader>
                          {selectedProduct && (
                            <div className="space-y-6">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                  <h4 className="font-semibold text-slate-900 mb-2">Specifications</h4>
                                  <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                      <span>Capacity:</span>
                                      <span className="font-medium">{selectedProduct.capacity}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span>Material:</span>
                                      <span className="font-medium">{selectedProduct.material}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span>Diameter:</span>
                                      <span className="font-medium">{selectedProduct.specifications.diameter}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span>Height:</span>
                                      <span className="font-medium">{selectedProduct.specifications.height}</span>
                                    </div>
                                  </div>
                                </div>
                                
                                <div>
                                  <h4 className="font-semibold text-slate-900 mb-2">Inventory & Pricing</h4>
                                  <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                      <span>Price per unit:</span>
                                      <span className="font-medium text-green-600">${selectedProduct.inventory.pricePerUnit.toFixed(3)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span>Units per case:</span>
                                      <span className="font-medium">{selectedProduct.inventory.unitsPerCase}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              
                              <div>
                                <h4 className="font-semibold text-slate-900 mb-2">Features</h4>
                                <div className="grid grid-cols-2 gap-2">
                                  {selectedProduct.features.map(feature => (
                                    <div key={feature} className="flex items-center text-sm">
                                      <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                                      {feature}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </DialogContent>
                      </Dialog>
                      
                      {/* Show internal cart for logged-in users, Shopify for guests */}
                      <div className="flex-1 space-y-3">
                        {user ? (
                          // Logged-in users: Internal cart system
                          <>
                            <div className="flex items-center justify-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => updateQuantity(product.id, getQuantity(product.id) - 1)}
                                disabled={getQuantity(product.id) <= 1}
                              >
                                <Minus className="w-4 h-4" />
                              </Button>
                              <Input
                                type="number"
                                min="1"
                                value={getQuantity(product.id)}
                                onChange={(e) => updateQuantity(product.id, parseInt(e.target.value) || 1)}
                                className="h-8 w-16 text-center"
                              />
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => updateQuantity(product.id, getQuantity(product.id) + 1)}
                              >
                                <Plus className="w-4 h-4" />
                              </Button>
                              <span className="text-xs text-slate-600">cases</span>
                            </div>

                            <Button
                              className="w-full font-semibold"
                              style={{ background: 'linear-gradient(135deg, #7C3AED, #F97316)' }}
                              onClick={() => {
                                const quantity = getQuantity(product.id);
                                if (cart.addToCart(product, quantity)) {
                                  toast.success(`Added ${quantity} case${quantity > 1 ? 's' : ''} to cart`);
                                }
                              }}
                            >
                              <ShoppingCart className="w-4 h-4 mr-2" />
                              Add to Cart
                            </Button>

                            <p className="text-xs text-center text-slate-500">
                              Order on account • Billed {cart.periodSummary?.next_billing_date ? 'on ' + new Date(cart.periodSummary.next_billing_date).toLocaleDateString() : 'bi-weekly'}
                            </p>
                          </>
                        ) : (
                          // Guest users: Shopify checkout
                          hasShopifyIntegration(product.id) ? (
                            <div className="w-full space-y-2">
                              <p className="text-xs text-center text-blue-600 font-medium">
                                ✓ Pay now with Shopify
                              </p>
                              
                              <ShopifyBuyButton 
                                productId={getShopifyProductId(product.id)}
                                variantId={getShopifyVariantId(product.id)}
                                containerId={`shopify-buy-${product.id}`}
                              />
                              
                              <p className="text-xs text-center text-slate-500">
                                Secure checkout • No account required
                              </p>
                              
                              <p className="text-xs text-center text-purple-600 font-medium mt-3">
                                <a href="/register" className="hover:underline">
                                  Register for credit ordering →
                                </a>
                              </p>
                            </div>
                          ) : (
                            <Button
                              variant="outline"
                              className="w-full"
                              onClick={() => navigate('/register')}
                            >
                              Register to Order
                            </Button>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {filteredProducts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-slate-500 text-lg">No products found matching your criteria.</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => {
                  setSearchQuery('');
                  setCategoryFilter('all');
                }}
              >
                Clear Filters
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Browse Full Catalog CTA */}
      <section className="py-12 px-4 bg-gradient-to-br from-slate-100 to-slate-50">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-white rounded-2xl p-8 sm:p-12 shadow-lg border border-slate-200">
            <Package className="w-12 h-12 mx-auto mb-4 text-purple-600" />
            <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
              Need Something Else?
            </h3>
            <p className="text-slate-600 mb-6 text-base sm:text-lg">
              Browse our complete catalog with over 100+ pharmaceutical supply products
            </p>
            <Button 
              size="lg"
              variant="outline"
              className="border-2 border-purple-600 text-purple-700 hover:bg-purple-50 font-semibold px-8 py-6 text-base sm:text-lg rounded-lg"
              onClick={() => window.location.href = '/advance-orders'}
            >
              <Package className="w-5 h-5 mr-2" />
              Browse Full Catalog
            </Button>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-slate-800 to-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Get in Touch</h2>
            <p className="text-lg text-slate-300">Ready to place an order or need a custom quote? We&apos;re here to help.</p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="space-y-8">
              <div>
                <h3 className="text-xl font-semibold text-white mb-6">Contact Information</h3>
                <div className="space-y-4">
                  {[
                    { icon: MapPin, label: "Service Area", value: "Serving Calgary and surrounding areas" },
                    { icon: Phone, label: "Phone", value: companyInfo.phone },
                    { icon: Mail, label: "Email", value: companyInfo.email }
                  ].map((item, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <item.icon className="w-5 h-5 text-purple-400 mt-1" />
                      <div>
                        <p className="font-medium text-white">{item.label}</p>
                        <p className="text-slate-300">{item.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-white mb-4">Why Choose Us</h3>
                <div className="space-y-3">
                  {companyInfo.services.map((service, index) => (
                    <div key={index} className="flex items-start text-slate-300">
                      <CheckCircle className="w-5 h-5 text-green-400 mr-3 mt-0.5 flex-shrink-0" />
                      <span>{service}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="bg-slate-800/50 rounded-lg p-6 border border-slate-700">
                <div className="flex items-start space-x-3 mb-4">
                  <Shield className="w-6 h-6 text-purple-400 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-white mb-2">Licensed & Trusted</h4>
                    <p className="text-sm text-slate-300">
                      Licensed pharmaceutical supplier in Alberta. All products meet Health Canada requirements and safety standards.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <Card className="bg-white shadow-xl border-0">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-2xl">Send us a Message</CardTitle>
                <p className="text-sm text-slate-600 mt-2">Get a custom quote or ask about our products</p>
              </CardHeader>
              <CardContent className="pt-6">
                <form onSubmit={handleContactSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name" className="text-slate-700 font-medium">Name *</Label>
                      <Input 
                        id="name"
                        required
                        value={contactForm.name}
                        onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-slate-700 font-medium">Email *</Label>
                      <Input 
                        id="email"
                        type="email"
                        required
                        value={contactForm.email}
                        onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="phone">Phone</Label>
                      <Input 
                        id="phone"
                        value={contactForm.phone}
                        onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="company">Pharmacy/Company</Label>
                      <Input 
                        id="company"
                        value={contactForm.company}
                        onChange={(e) => setContactForm({...contactForm, company: e.target.value})}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="message">Message *</Label>
                    <Textarea 
                      id="message"
                      required
                      rows={4}
                      value={contactForm.message}
                      onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                      placeholder="Tell us about your needs, quantities, timeline, or any questions..."
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full relative overflow-hidden group font-semibold"
                    style={{ background: 'linear-gradient(135deg, #7C3AED, #F97316)' }}
                  >
                    <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity"></div>
                    <span className="relative">Send Message</span>
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 px-4 sm:px-6 lg:px-8 border-t border-slate-800">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center space-x-4 mb-6">
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_alberta-pharmacy-1/artifacts/euyis7mj_Logo%20-%20APS%20Icon%20-%20Large.png"
                alt="Alberta Pharmacy Supply Logo"
                className="w-16 h-16 object-contain"
              />
            </div>
            <span className="text-2xl font-bold">{companyInfo.name}</span>
          </div>
          <p className="text-slate-300 mb-2 text-lg">{companyInfo.tagline}</p>
          <p className="text-slate-400 mb-4">Serving Calgary and surrounding areas</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-sm text-slate-400">
            <span>© 2025 Alberta Pharmacy Supply. All rights reserved.</span>
            <span className="hidden sm:inline">|</span>
            <span>Licensed pharmaceutical supplier in Alberta, Canada</span>
          </div>
        </div>
      </footer>

      {/* Cart Modal */}
      <CartModal
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cart={cart}
        onUpdateQuantity={cart.updateQuantity}
        onRemoveItem={cart.removeFromCart}
        onCheckout={async () => {
          cart.clearCart();
          await cart.loadCurrentCycle();
        }}
        user={user}
      />
    </div>
  );
};

export default HomePage;